import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { CerebroHeading, CerebroLabel, CerebroText } from '../../shared/components';
import { Deal, DealAnalysisMetricType, DealFinancialMetric } from '../../shared/models';
import { IncomeStatementHeadings } from './IncomeStatementHeadings';
import { FinancialCoulmnsProps, getFinancialValue, IncomeStatementRow } from './IncomeStatementRow';
import NumberFormat from 'react-number-format';

interface FinancialInformationProps {
	isLoading: boolean;
	deal: Deal | null | undefined;
	financialMetrics: DealFinancialMetric[];
	analysisType: DealAnalysisMetricType | undefined;
	showHeading: boolean;
}

export const FinancialInformation: React.FC<FinancialInformationProps> = ({
	isLoading,
	deal,
	financialMetrics,
	analysisType,
	showHeading,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const twoYearsData = financialMetrics.filter(x => x.twoYearsAgo && x.twoYearsAgo > 0).length > 0;
	const previousYearData = financialMetrics.filter(x => x.previousFullYear && x.previousFullYear > 0).length > 0;
	const projectedFullYearData =
		financialMetrics.filter(x => x.projectedFullYear && x.projectedFullYear > 0).length > 0;
	const futureYearData = financialMetrics.filter(x => x.futureYear && x.futureYear > 0).length > 0;
	const columnData = {
		twoYearsAnyData: twoYearsData,
		previousYearAnyData: previousYearData,
		projectedFullYearAnyData: projectedFullYearData,
		futureYearAnyData: futureYearData,
	} as FinancialCoulmnsProps;

	return (
		<>
			{showHeading && (
				<CerebroHeading size="h1" component="h1" sx={{ fontWeight: 'bold' }}>
					{getAnalysisTypeDescription(analysisType)}
				</CerebroHeading>
			)}

			<CerebroHeading size="h2" gutterBottom>
				P&#38;L / Cashflow Details
			</CerebroHeading>

			<IncomeStatementHeadings isLoading={isLoading} deal={deal} columns={columnData} />

			<Grid container alignItems="center" spacing={2} sx={{ marginBottom: 6 }}>
				<IncomeStatementRow
					financialMetrics={financialMetrics}
					isLoading={isLoading}
					metricType="TotalRevenue"
					deal={deal}
					analysisType={analysisType}
					columns={columnData}
				/>
				<IncomeStatementRow
					financialMetrics={financialMetrics}
					isLoading={isLoading}
					metricType="NetIncome"
					deal={deal}
					analysisType={analysisType}
					columns={columnData}
				/>
				<IncomeStatementRow
					financialMetrics={financialMetrics}
					isLoading={isLoading}
					metricType="Ebitda"
					deal={deal}
					analysisType={analysisType}
					columns={columnData}
				/>

				<IncomeStatementRow
					financialMetrics={financialMetrics}
					isLoading={isLoading}
					metricType="Adjustments"
					deal={deal}
					analysisType={analysisType}
					columns={columnData}
				/>

				<IncomeStatementRow
					financialMetrics={financialMetrics}
					isLoading={isLoading}
					metricType="AdjustedEbitda"
					deal={deal}
					analysisType={analysisType}
					columns={columnData}
				/>

				{deal?.haveRecurringRevenue && analysisType == 'Borrower' && (
					<IncomeStatementRow
						financialMetrics={financialMetrics}
						isLoading={isLoading}
						metricType="Mrr"
						deal={deal}
						analysisType={analysisType}
						columns={columnData}
					/>
				)}
				{deal?.acquisitionHaveRecurringRevenue && analysisType == 'Acquisition' && (
					<>
						<Grid item xs={12}>
							<hr />
						</Grid>
						<IncomeStatementRow
							financialMetrics={financialMetrics}
							isLoading={isLoading}
							metricType="Mrr"
							deal={deal}
							analysisType={analysisType}
							columns={columnData}
						/>
					</>
				)}

				<Grid item xs={12}>
					<hr />
				</Grid>

				<Grid item xs={12}>
					<CerebroHeading size="h2">Balance Sheet</CerebroHeading>
				</Grid>
				<BalanceSheetHeading label={'Most Recent Quarter'} value={0} isMobile={isMobile} />

				<FinancialRow
					label={'Cash & Marketables'}
					value={getFinancialValue(financialMetrics, 'trailingTwelveMonths', analysisType, 'Cash')}
					isMobile={isMobile}
				/>
				<FinancialRow
					label={'Accounts Receivable'}
					value={getFinancialValue(
						financialMetrics,
						'trailingTwelveMonths',
						analysisType,
						'AccountsReceivables'
					)}
					isMobile={isMobile}
				/>

				<br />
				<Grid item xs={12}>
					<CerebroHeading size="h2">Tangible Assets</CerebroHeading>
				</Grid>
				<BalanceSheetHeading label={'Most Recent Quarter'} value={0} isMobile={isMobile} />

				<FinancialRow
					label={'Inventory'}
					value={getFinancialValue(financialMetrics, 'trailingTwelveMonths', analysisType, 'Inventory')}
					isMobile={isMobile}
				/>
				<FinancialRow
					label={'Owner Occupied Real Estate'}
					value={getFinancialValue(financialMetrics, 'trailingTwelveMonths', analysisType, 'RealEstate')}
					isMobile={isMobile}
				/>
				<FinancialRow
					label={'Furniture / Fixtures'}
					value={getFinancialValue(
						financialMetrics,
						'trailingTwelveMonths',
						analysisType,
						'FurnitureFixture'
					)}
					isMobile={isMobile}
				/>
				<FinancialRow
					label={'Machinery & Equipment'}
					value={getFinancialValue(financialMetrics, 'trailingTwelveMonths', analysisType, 'Equipment')}
					isMobile={isMobile}
				/>
				<FinancialRow
					label={'Rolling Stock / Vehicles'}
					value={getFinancialValue(financialMetrics, 'trailingTwelveMonths', analysisType, 'Vehicles')}
					isMobile={isMobile}
				/>
				<FinancialRow
					label={'Marine Vessels'}
					value={getFinancialValue(financialMetrics, 'trailingTwelveMonths', analysisType, 'MarineVessels')}
					isMobile={isMobile}
				/>
				<FinancialRow
					label={'Helicopters'}
					value={getFinancialValue(financialMetrics, 'trailingTwelveMonths', analysisType, 'Helicopters')}
					isMobile={isMobile}
				/>
				<FinancialRow
					label={'Fixed Aircraft'}
					value={getFinancialValue(
						financialMetrics,
						'trailingTwelveMonths',
						analysisType,
						'FixedWingAircraft'
					)}
					isMobile={isMobile}
				/>
			</Grid>
		</>
	);
};

function getAnalysisTypeDescription(analysisType: DealAnalysisMetricType | undefined): string {
	if (analysisType && analysisType == 'Acquisition') {
		return 'Acquisition Target';
	}

	return 'Borrower';
}

interface FinancialRowProps {
	label: string;
	value: number;
	isMobile: boolean;
}

const BalanceSheetHeading: React.FC<FinancialRowProps> = ({ label, isMobile }) => {
	if (isMobile) return <></>;

	return (
		<Grid container spacing={2} sx={{ marginY: 1 }}>
			<Grid item md={2}></Grid>
			<Grid item md={2}>
				<CerebroText align="right" weight="bold" variant="small">
					{label}
				</CerebroText>
			</Grid>
			<Grid item md={6}></Grid>
		</Grid>
	);
};

const FinancialRow: React.FC<FinancialRowProps> = ({ label, value, isMobile }) => {
	return (
		<>
			<Grid item xs={12} md={2} sx={{ display: 'flex' }} alignItems="center">
				<CerebroHeading align="right" size="h3" gutterBottom sx={{ marginTop: { xs: 1, md: 0 } }}>
					{label}
					{isMobile && <span> (Most Recent Quarter)</span>}
				</CerebroHeading>
			</Grid>
			<Grid item xs={12} md={2}>
				<CerebroText align="right" weight="medium">
					<NumberFormat value={value} displayType="text" thousandSeparator={true} prefix="$" />
				</CerebroText>
			</Grid>
			<GridBreak />
		</>
	);
};

export const GridBreak: React.FC = () => {
	return <Box sx={{ width: '100%' }}></Box>;
};
